import { Link, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, Building2, Image } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/shadcn/button';
import { cn } from '@/lib/utils';
import { useSystemBarsStyle, systemBarsIconsForBackground } from '@/hooks/useSystemBarsStyle';

const menuItems = [
  { path: '/superadmin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/superadmin/arenas', label: 'Arena', icon: Building2 },
  { path: '/superadmin/banners', label: 'Banner', icon: Image },
];

export default function SuperAdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isActive = (path: string, exact?: boolean) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="flex min-h-dvh flex-col md:flex-row">
      <div className="block md:hidden">
        <MobileShell userName={user?.name} onLogout={logout} isActive={isActive} />
      </div>
      <div className="hidden md:flex md:flex-1">
        <DesktopShell userName={user?.name} onLogout={logout} isActive={isActive} />
      </div>
    </div>
  );
}

interface ShellProps {
  userName?: string;
  onLogout: () => void;
  isActive: (path: string, exact?: boolean) => boolean;
}

function DesktopShell({ userName, onLogout, isActive }: ShellProps) {
  return (
    <>
      <aside className="flex w-64 flex-col border-r bg-white">
        <div className="border-b p-4">
          <h1 className="text-lg font-bold text-primary">Sporto Admin</h1>
          <p className="text-sm text-muted-foreground">{userName}</p>
        </div>
        <nav className="flex-1 space-y-1 p-4">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive(item.path, item.exact)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-accent'
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="border-t p-4">
          <Button variant="outline" className="w-full" onClick={onLogout}>
            Keluar
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto bg-gc-50 p-6">
        <Outlet />
      </main>
    </>
  );
}

function MobileShell({ userName, onLogout, isActive }: ShellProps) {
  useSystemBarsStyle(systemBarsIconsForBackground('light'));

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b bg-white px-4 pt-safe">
        <div className="flex items-center justify-between py-3">
          <div>
            <h1 className="font-bold text-primary">Sporto Admin</h1>
            <p className="text-xs text-muted-foreground">{userName}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout}>
            Keluar
          </Button>
        </div>
      </header>
      <main className="flex-1 overflow-auto bg-gc-50 p-4">
        <Outlet />
      </main>
      <nav className="flex border-t bg-white pb-safe">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={cn(
              'flex flex-1 flex-col items-center gap-1 py-2 text-xs',
              isActive(item.path, item.exact) ? 'text-primary' : 'text-muted-foreground'
            )}
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
